import { closeX } from './popup.js';
import { $, S, el } from '../state/store.js';
import { exportPNG } from '../export/png.js';
import { exportMP4 } from '../export/mp4.js';
import { exportMP4Panels, exportPNGPanels, wholeWallSize } from '../export/multipanel.js';
import { peakScreenRes, screenCount } from '../scene/panels.js';
import {
  FLAT_GAP_MAX, FLAT_MARGIN_MAX, RESOLVE_SECONDS, clampFlatGap, clampFlatMargin, clampResolveAt, clampResolveStop,
  exportOnSiteMP4, exportOnSitePNG, flatPreview, onsitePanelCount, onsiteSize
} from '../export/onsite.js';
import {
  MOTION_BLUR_STEPS, canEncodeAt, canExportVideo, encodeSize, estimateSeconds,
  exportMP4WithUI, motionBlurSamples
} from '../export/mp4.js';
import {
  EXPORT_COST, canAfford, canExportVideoFiles, hasLicence,
  setVideoWatermark, videoWatermarkOn
} from '../services/limits.js';
import { creditBalance } from '../services/entitlements.js';
import { showBilling } from './billing.js';
import { segRow } from './controls.js';
import { frameCount, hasMotion, pause } from '../state/timeline.js';
import { toast } from './toast.js';
import { chooseSiteClip, clearSiteClip, siteClipInfo, siteClipReady } from '../export/site-clip.js';

/* EXPORT — one screen, two deliverables.

   Used to be two separate, unequally-discoverable buttons: the stepper's own
   "Export" ran the PNG directly, and a second, differently-styled "Export
   MP4" button lived in the timeline dock's own toolbar, along the bottom
   row — a place nothing else points at and easy to never notice exists. One
   screen, opened from the one place someone already goes to export
   something, is what "export" being a single word on the stepper should
   have meant from the start.

   Image renders immediately, the same way it always did. Video still goes
   through the same estimate-and-confirm gate — that check has to run before
   this screen closes, not after, so declining it leaves the picker open
   rather than dropping someone back at a bare viewport wondering what just
   happened. */

let overlay = null;
function ensureOverlay() {
  if (overlay) return overlay;
  overlay = el('div', 'authwrap hidden');
  overlay.id = 'exportwrap';
  document.body.appendChild(overlay);
  return overlay;
}
const hide = () => ensureOverlay().classList.add('hidden');
const show = () => ensureOverlay().classList.remove('hidden');

/* Anything with at least one keyframe track — the same test the timeline
   dock itself uses to decide whether it has anything to show. */
/* hasMotion now lives in state/timeline.js — the watermark preview needs the
   same test, and two copies would drift. */

const IMG_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="1.5" y="2.5" width="13" height="11" rx="1.2"/><circle cx="5.4" cy="6.2" r="1.1"/><path d="M2 11.5 6 8l2.3 2 2.7-3 3 3.5"/></svg>';
const VID_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="1.5" y="3.5" width="9.5" height="9" rx="1.2"/><path d="M11 6.3 14.5 4v8l-3.5-2.3z" stroke-linejoin="round"/></svg>';

/* Remembered between openings: someone exporting a wall usually wants the same
   answer twice in a row, and re-picking it every time reads as the app having
   forgotten. Module-level rather than in S — it is a choice about this export,
   not a property of the project. */
let arrayOutput = 'screens';

export function showExport() {
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard exportcard');
  card.appendChild(el('h2', null, 'Export'));
  closeX(card, hide);
  card.appendChild(el('p', 'authnote', 'Choose a deliverable.'));

  /* Named, because the two pairs are different KINDS of output: one is the
     file that goes on the screen, the other a picture of it on the building.
     Unlabelled and equally sized they read as four alternatives. */
  const gDeliver = el('div', 'expgroup');
  gDeliver.appendChild(el('h3', null, 'Delivery'));

  /* A SCREEN ARRAY HAS TWO ANSWERS, and until now it silently gave one. The
     files that go on the wall are one per screen — that is the deliverable, and
     it is what the players take. But the wall as a single picture is the thing
     you send someone to look at, and there was no way to ask for it even though
     the app renders exactly that in Flat.

     Offered as a choice rather than a fifth card, because it is not another
     deliverable: it changes what Image and Video below mean. The cards restate
     it so the answer is visible at the moment of clicking, not only up here. */
  const arr = S.screenArray;
  if(arr){
    const n = screenCount(arr);
    const ws = wholeWallSize();
    const free = arr.mode === 'free';
    const seg = segRow('Output', arrayOutput, [
      ['screens', `Each screen · ${n} files`],
      ['wall',    `Whole wall · 1 file`]
    ], v=>{ arrayOutput = v; showExport(); });
    gDeliver.appendChild(seg);
    /* "At W × H" only holds for a grid, where every screen shares one
       resolution — a free wall's screens may not, so it names the count
       instead of a size that would be true of at most one of them. */
    gDeliver.appendChild(el('p', 'note', arrayOutput === 'screens'
      ? (free
          ? `One file per screen, each at its own resolution, zipped with a note naming which goes where. The gaps are in none of them — each screen shows only what it can see.`
          : `One file per screen at ${arr.res.w} × ${arr.res.h}, zipped with a note naming which goes where. The margins are in none of them — each screen shows only what it can see.`)
      : `The whole wall as one ${ws.w} × ${ws.h} file, for showing someone. The ${free ? 'gaps are' : 'margins are'} in it, as they will look. Not the file the players take — that is the other option.`));
  }

  /* THE MODEL, PUT TO THE PERSON. Every tool is open and a still is always
     free. What a credit pack changes is the VIDEO file: it unlocks watermarked
     video for 12 months (0030), and credits are what take the watermark off.
     That is said here, once, rather than left to be found out by trying.

     The switch below is for somebody who already has video — a person who has
     not bought anything has nothing to switch, and is told what unlocks it
     instead. On by default and on every time this opens; see
     services/limits.js for why it is not remembered. */
  const videoAccess = canExportVideoFiles();
  const unlocked = videoAccess.ok;
  const markOn = videoWatermarkOn();
  const licensedNow = hasLicence();
  const panelled = (arr && arrayOutput === 'screens') || !!S.cornerPanel;
  const videoCost = panelled ? EXPORT_COST.videoPanels : EXPORT_COST.video;
  const priceLine = licensedNow
    ? 'included in your licence'
    : `${EXPORT_COST.video} credits, ${EXPORT_COST.videoPanels} for a multi-screen video, `
      + `${EXPORT_COST.onsiteVideo} for an on-site preview — you have ${creditBalance()}`;
  const wmWrap = el('div', 'expblur expmark');
  if (unlocked) {
    const wmLab = el('label', 'expblur__row');
    wmLab.appendChild(el('span', null, 'Watermark'));
    const wmBox = el('input'); wmBox.type = 'checkbox'; wmBox.checked = markOn;
    wmBox.id = 'expWatermark';
    wmLab.appendChild(wmBox);
    /* Not for a licence holder: the advice is about spending credits, and they
       are shown none. */
    if (!licensedNow) wmLab.appendChild(el('span', 'expblur__hint', 'Test with the watermark on before spending credits.'));
    wmWrap.appendChild(wmLab);
    wmWrap.appendChild(el('p', 'note', markOn
      ? `Videos carry a moving watermark and cost nothing. Turn it off for a clean video: ${priceLine}. `
        + 'Stills are free and clean, with commercial rights included.'
      : `Clean video: ${priceLine}. Stills are free and clean, with commercial rights included.`));
    /* Rebuilt rather than patched, as the wall/screens choice above is: the
       cards below change colour and wording with it, and one rebuild keeps them
       agreeing with each other. */
    wmBox.onchange = () => { setVideoWatermark(wmBox.checked); showExport(); };
  } else {
    /* The one branch where commercial use is a real distinction rather than a
       given — a licence or a live pack covers it (the `if` above says so
       outright: "with commercial rights included"), so this is said only to
       the account it is actually true for: one that has never bought, or
       whose pack's 12 months have ended. Stills exported while the pack was
       live keep their commercial rights — only new exports are affected. */
    wmWrap.appendChild(el('p', 'note',
      `${videoAccess.reason} `
      + `A clean video then costs ${EXPORT_COST.video} credits, ${EXPORT_COST.videoPanels} for a multi-screen one `
      + `and ${EXPORT_COST.onsiteVideo} for an on-site preview. `
      + 'Stills are free for non-commercial use — a credit pack adds commercial rights for 12 months.'));
  }
  gDeliver.appendChild(wmWrap);

  const choices = el('div', 'exportchoice');
  gDeliver.appendChild(choices);
  card.appendChild(gDeliver);

  /* What a card says about money — always, because "how much" is the first
     thing anybody wants to know and the answer is never nothing to say. Free
     stills say free. A video says what the watermark switch above has made
     true: free with the mark, or its price without it. */
  /* A TAG, not a fragment of the subtitle. What a video costs and what is left
     afterwards are the two numbers somebody is deciding on, so they get their
     own element that reads as one glance: "−10 credits | 15 left". Kept out of
     the subtitle for a practical reason too — the motion-blur row rewrites that
     span every time its dropdown changes, and money should not be something a
     dropdown can wipe. An <em>, not a <span>, so that rewrite cannot find it. */
  const spendTag = (cost) => {
    const bal = creditBalance();
    /* No word "credits" for a licence holder, even in a tooltip: they are shown
       none anywhere. */
    if (markOn) return `<em class="exptag exptag--free"${licensedNow ? '' : ' title="No credits are spent"'}>Free · watermarked</em>`;
    if (licensedNow) return '<em class="exptag" title="Your licence covers a clean video">Included in your licence</em>';
    return canAfford(cost)
      ? `<em class="exptag exptag--spend" title="Spends ${cost} credits, leaving ${bal - cost}">`
        + `<b>\u2212${cost}</b> credits<i>${bal - cost} left</i></em>`
      : `<em class="exptag exptag--short" title="This costs ${cost} credits and you have ${bal}">`
        + `<b>\u2212${cost}</b> credits<i>you have ${bal}</i></em>`;
  };
  /* A clean video that cannot be paid for is stopped HERE, before a render
     that would end in a marked file nobody asked for. */
  const cannotPay = (cost) => {
    if (markOn || licensedNow || canAfford(cost)) return false;
    toast(`A clean video costs ${cost} credits and you have ${creditBalance()}. `
      + 'Leave the watermark on to export it free.', true, { text: 'Buy credits', onClick: showBilling });
    return true;
  };
  // ── Image ──────────────────────────────────────────────────────────────
  const img = el('button', 'expopt');
  /* Said on the card, because with an animation there is no single "the
     picture": a still is the frame the playhead is on, and somebody who
     scrubbed to 2.3s and expects that frame should be told before they click. */
  const atFrame = () => hasMotion() ? ` · frame at ${(+S.playhead).toFixed(1)}s` : '';
  const imgSub = () => {
    if(!arr) return `PNG · ${S.res.w} × ${S.res.h}${atFrame()}`;
    const ws = wholeWallSize();
    if(arrayOutput === 'wall') return `PNG · ${ws.w} × ${ws.h} · one file${atFrame()}`;
    const n = screenCount(arr);
    return (arr.mode === 'free'
      ? `PNG · sizes vary · ${n} files`
      : `PNG · ${arr.res.w} × ${arr.res.h} · ${n} files`) + atFrame();
  };
  img.innerHTML = IMG_ICON + '<b>Image</b>' + `<span>${imgSub()} · free</span>`;
  img.onclick = () => runPNG(img);
  choices.appendChild(img);

  // ── Video ──────────────────────────────────────────────────────────────
  /* Two independent reasons this can be unavailable, same as the timeline
     dock's own export button used to distinguish — "nothing to render" and
     "this browser can't encode video at all" (WebCodecs is Chromium-family
     only) want different explanations, not one generic greyed-out state. */
  const motion = hasMotion();
  const canEncode = canExportVideo();
  /* Three independent things now: the browser can encode it, the account has
     unlocked video, and there is an animation to render. Nothing about the
     TOOLS is a reason any more — everybody can build one. */
  const ready = motion && canEncode && unlocked;
  /* One place decides WHY video is unavailable, so the two cards below can
     never answer the same question differently — they did not diverge, but
     they were two copies of a ternary waiting to. */
  /* Ordered by how permanent each reason is. The browser cannot be argued
     with. The lock is the biggest fact about a fresh account and is worth
     telling before the smaller one, "nothing is animated yet" — which a
     person can fix in a minute and would otherwise fix only to meet the lock
     afterwards. */
  const vidWhy = () => !canEncode ? 'This browser cannot encode video — Chrome, Edge or Safari can.'
    : !unlocked ? videoAccess.reason
    : 'Nothing is animated yet — add a keyframe in Motion first.';
  const vidOffSub = () => !canEncode ? 'Not supported in this browser'
    : !unlocked ? 'Unlocks with a credit pack'
    : 'Nothing is animated yet';
  /* Only worth selling to when buying something would change the answer: the
     lock does. An unsupported browser does not, and neither does an empty
     timeline — which anybody can fill for nothing. */
  const vidSell = () => (canEncode && !unlocked)
    ? { text: 'Buy credits', onClick: showBilling } : undefined;
  /* Blue only when it is genuinely the paid path: a video that can be made,
     the watermark switched off, and something to charge. Under a licence it
     is included rather than spent, but it is still the clean file, so it takes
     the same colour and says "included" instead of a number. */
  const payPath = ready && !markOn;
  const vid = el('button', 'expopt' + (ready ? '' : ' expopt--off') + (payPath ? ' expopt--pay' : ''));
  vid.id = 'expVideo';
  /* NOT a native disabled attribute. That was the exact shape of the bug
     this screen replaces — a disabled element swallows its own click event,
     so there is no way for the click handler to ever explain WHY, and
     silence is indistinguishable from broken. aria-disabled keeps the
     accessibility signal (assistive tech still announces it as disabled)
     without giving up the click. */
  vid.setAttribute('aria-disabled', String(!ready));
  const frames = frameCount();
  const vidSub = () => {
    const n = motionBlurSamples();
    /* Panels cost their own price — the wall and the corner both render and
       encode a file per panel, which exportMP4Panels charges for as one. */
    return `MP4 · ${frames} frames · ${S.timeline.fps}fps` + (n > 1 ? ` · blur ×${n}` : '');
  };
  vid.innerHTML = VID_ICON + '<b>Video</b>' +
    (ready ? `<span>${vidSub()}</span>${spendTag(videoCost)}` : `<span>${vidOffSub()}</span>`);
  vid.title = ready ? 'Render the timeline to an MP4' : vidWhy();
  vid.onclick = () => {
    if (ready) { if (!cannotPay(videoCost)) runMP4(); return; }
    /* The contact link only when the licence is what is missing — an
       unsupported browser or an empty timeline is not something to sell to. */
    toast(vidWhy(), true, vidSell());
  };
  choices.appendChild(vid);

  /* ── the mockup ────────────────────────────────────────────────────────
     The other two are the DELIVERABLE — the file that goes on the screen.
     These two are the picture of it on the building, which is what a client
     is shown and what a proposal carries. Offered only once there is a
     photograph and a solve, because without both there is no building to put
     it on. */
  if(S.photo && S.solved){
    const gSite = el('div', 'expgroup');
    gSite.appendChild(el('h3', null, 'On-site preview'));
    const siteChoices = el('div', 'exportchoice exportchoice--sub');
    gSite.appendChild(siteChoices);
    const { w: ow, h: oh } = onsiteSize();
    /* A FILMED VIDEO behind the render instead of the photograph (export/site-clip.js). Loaded in the
       calibration step, or here for a project that was opened afterwards. With one, a video is worth making
       even when nothing in the project is animated: it is the building that moves. */
    const clipInfo = siteClipInfo();
    const filmedOn = S.onsite.useFilmed && !!clipInfo && !clipInfo.problem;
    const siteReady = ready || (filmedOn && unlocked && canEncode);
    const shot = el('button', 'expopt');
    shot.innerHTML = IMG_ICON + '<b>On-site image</b>' +
      `<span>PNG · ${ow} × ${oh} · ${filmedOn ? 'over the filmed video · ' : ''}free</span>`;
    shot.title = filmedOn ? 'The render over the filmed video, as a still' : 'The render on the photograph, as a still';
    shot.onclick = () => runOnSitePNG(shot, filmedOn);
    siteChoices.appendChild(shot);

    const shotVid = el('button', 'expopt' + (siteReady ? '' : ' expopt--off') + (siteReady && !markOn ? ' expopt--pay' : ''));
    shotVid.setAttribute('aria-disabled', String(!siteReady));
    shotVid.innerHTML = VID_ICON + '<b>On-site video</b>' +
      (siteReady ? `<span>MP4 · ${ow} × ${oh} · ${frames} frames${filmedOn ? ' · over the filmed video' : ''}</span>${spendTag(EXPORT_COST.onsiteVideo)}` : `<span>${vidOffSub()}</span>`);
    shotVid.title = siteReady ? (filmedOn ? 'The render over the filmed video, over the timeline' : 'The render on the photograph, over the timeline') : vidWhy();
    shotVid.onclick = () => {
      if(siteReady){ if(!cannotPay(EXPORT_COST.onsiteVideo)) runOnSiteMP4(filmedOn); return; }
      toast(vidWhy(), true, vidSell());
    };
    siteChoices.appendChild(shotVid);

    /* The filmed video: choose, use, change or remove. */
    const filmWrap = el('div', 'expblur');
    const fRow = el('div', 'expblur__row');
    fRow.appendChild(el('span', null, 'Filmed video'));
    const pick = el('input'); pick.type = 'file'; pick.className = 'sr';
    pick.accept = 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm';
    pick.onchange = () => {
      const f = pick.files[0]; pick.value = '';
      if(!f) return;
      chooseSiteClip(f).then(() => { S.onsite.useFilmed = true; showExport(); }, e => toast(e.message || 'That video could not be used.', true));
    };
    const choose = el('button', 'btn btn--sm btn--gh', clipInfo ? 'Change…' : 'Choose video…');
    choose.type = 'button'; choose.onclick = () => pick.click();
    if(clipInfo && !clipInfo.problem){
      const box = el('input'); box.type = 'checkbox'; box.checked = S.onsite.useFilmed; box.id = 'expFilmed';
      box.title = 'Render over the filmed video';
      box.onchange = () => { S.onsite.useFilmed = box.checked; showExport(); };
      fRow.appendChild(box);
    }
    fRow.appendChild(choose);
    if(clipInfo){
      const drop = el('button', 'btn btn--sm btn--gh', 'Remove'); drop.type = 'button';
      drop.onclick = () => { clearSiteClip(); showExport(); };
      fRow.appendChild(drop);
    }
    fRow.appendChild(pick);
    filmWrap.appendChild(fRow);
    filmWrap.appendChild(el('p', 'note', !clipInfo
      ? 'Render the on-site preview over a video of the building instead of the photograph. Use a video from the same camera, framed like the photo, with the camera held still. You can also load one in the first step, in place of the photo.'
      : clipInfo.problem
        ? clipInfo.problem
        : `${clipInfo.name} · ${clipInfo.w} × ${clipInfo.h} · ${clipInfo.duration.toFixed(1)} s. The video plays under the animation and starts over when the animation is longer. `
          + 'It is not saved with the project, so choose it again after reopening.'));
    gSite.appendChild(filmWrap);

    /* RESOLVE TO FLAT. The mockup plays on the building, then the render
       corner-pins out of the photograph until it is the deliverable, squared
       up and filling the frame. One file that says both "here is how it looks
       there" and "here is what you are being sent".

       A setting under its card, the same shape the motion-blur row has, rather
       than a third card: it changes what the video card above already does. */
    if(siteReady){
      const resolveWrap = el('div', 'expblur');
      const lab = el('label', 'expblur__row');
      lab.appendChild(el('span', null, 'Resolve to flat'));
      const box = el('input'); box.type = 'checkbox'; box.checked = S.onsite.resolveToFlat;
      lab.appendChild(box);
      resolveWrap.appendChild(lab);
    /* WHEN it starts. Offered as a time rather than a fraction because the
       timeline is in seconds and the playhead reads in seconds — "start it at
       4s" is a thing you can check against the dock, where "at 62%" is not.
       Clamped on the way in so the move always finishes inside the clip. */
      const dur = S.timeline.duration;
      const atRow = el('label', 'expblur__row');
      atRow.appendChild(el('span', null, 'Starts at'));
      const atIn = el('input', 'inp');
      atIn.type = 'number'; atIn.step = '0.1'; atIn.min = '0';
      atIn.max = String(Math.max(0, dur - RESOLVE_SECONDS).toFixed(1));
      const startDefault = () => clampResolveAt(S.onsite.resolveAt == null ? dur / 2 : S.onsite.resolveAt, dur, RESOLVE_SECONDS);
      atIn.value = String(+startDefault().toFixed(1));
      atRow.appendChild(atIn);
      resolveWrap.appendChild(atRow);

      /* AND BACK AGAIN, optionally. Empty means it stays flat, which is what
         this did before — so the field says so rather than sitting there as a
         blank nobody can interpret. */
      const stopRow = el('label', 'expblur__row');
      stopRow.appendChild(el('span', null, 'Back on site at'));
      const stopIn = el('input', 'inp');
      stopIn.type = 'number'; stopIn.step = '0.1';
      stopIn.placeholder = 'stays flat';
      stopRow.appendChild(stopIn);
      resolveWrap.appendChild(stopRow);
      /* Two different "no": the clip is too short to hold two moves at any
         start, or it is long enough but the start is too late to get back.
         The first hides the row — there is nothing to be done about it here.
         The second DISABLES it and says which start would work, because the
         fix is one field up and a control that silently vanished would leave
         somebody hunting for a feature they had been told about. */
      const latestStart = dur - 2 * RESOLVE_SECONDS;
      const couldFit = latestStart >= 0;
      const stopRoom = () => clampResolveStop(0, dur, RESOLVE_SECONDS, startDefault()) != null;
      const stopNow = () => (S.onsite.stopsAt == null ? null
        : clampResolveStop(S.onsite.stopsAt, dur, RESOLVE_SECONDS, startDefault()));

      const rNote = el('p', 'note');
      const paintNote = () => {
        const at = startDefault(), back = stopNow(), room = stopRoom();
        atRow.classList.toggle('hidden', !S.onsite.resolveToFlat);
        stopRow.classList.toggle('hidden', !S.onsite.resolveToFlat || !couldFit);
        stopRow.classList.toggle('is-off', !room);
        stopIn.disabled = !room;
        stopIn.min = String(+(at + RESOLVE_SECONDS).toFixed(1));
        stopIn.max = String(+Math.max(0, dur - RESOLVE_SECONDS).toFixed(1));
        stopIn.value = back == null ? '' : String(+back.toFixed(1));
        const out = `At ${(+at.toFixed(1))}s the render un-warps out of the photograph to the flat `
          + `deliverable over ${RESOLVE_SECONDS}s`;
        rNote.textContent = !S.onsite.resolveToFlat
          ? 'The mockup only. Turn this on to have it square up into the flat file part way '
            + 'through, which shows a client the thing and the deliverable in one go.'
          : back != null
            ? `At ${(+at.toFixed(1))}s it un-warps to the flat deliverable, holds, then at `
              + `${(+back.toFixed(1))}s pins back onto the building — ending where it started, `
              + `so the clip loops. Each move takes ${RESOLVE_SECONDS}s.`
            : !couldFit
              ? `${out} and the clip finishes there. The video stays the same length; the mockup `
                + `plays up to that point.`
              : room
                ? `${out} and the clip finishes there. Give it a time to come back on site and it `
                  + `will return to the building instead, ending where it began.`
                : `${out} and the clip finishes there. To have it come back on site as well, `
                  + `start it at ${(+latestStart.toFixed(1))}s or earlier — there is no room for `
                  + `the return leg after ${(+at.toFixed(1))}s in a ${(+dur.toFixed(1))}s clip.`;
      };
      atIn.onchange = () => {
        const v = parseFloat(atIn.value);
        S.onsite.resolveAt = Number.isFinite(v) ? clampResolveAt(v, dur, RESOLVE_SECONDS) : null;
        atIn.value = String(+startDefault().toFixed(1));
        /* The way out moved, so the way back may no longer fit behind it. */
        if(S.onsite.stopsAt != null) S.onsite.stopsAt = clampResolveStop(S.onsite.stopsAt, dur, RESOLVE_SECONDS, startDefault());
        paintNote();
      };
      stopIn.onchange = () => {
        const v = parseFloat(stopIn.value);
        S.onsite.stopsAt = Number.isFinite(v) ? clampResolveStop(v, dur, RESOLVE_SECONDS, startDefault()) : null;
        paintNote();
      };
      /* ADVANCED SETTINGS. How the flat file sits in the frame at the end of the
         resolve. Closed by default, because most exports want the default (it
         fills the frame), and only offered while Resolve to flat is on: with it
         off there is no flat file to place. */
      const adv = el('details', 'expadv');
      adv.open = advOpen;
      adv.appendChild(el('summary', null, 'Advanced settings'));
      adv.addEventListener('toggle', () => { advOpen = adv.open; });

      const numRow = (label, max, get, set) => {
        const row = el('label', 'expblur__row');
        row.appendChild(el('span', null, label));
        const i = el('input', 'inp expnum');
        i.type = 'number'; i.min = '0'; i.max = String(max); i.step = '1';
        i.value = String(+get().toFixed(1));
        const unit = el('span', 'expblur__hint', '% of the frame');
        i.onchange = () => {
          set(parseFloat(i.value));
          i.value = String(+get().toFixed(1));
          paintAdv();
        };
        row.append(i, unit);
        return row;
      };
      adv.appendChild(numRow('Margin around the flat file', FLAT_MARGIN_MAX, () => S.onsite.flatMargin, v => { S.onsite.flatMargin = clampFlatMargin(v); }));
      /* Between SCREENS, so only when there are two. A single screen has no
         neighbour, and a video wall resolves as one picture. */
      const twoScreens = onsitePanelCount() > 1;
      if(twoScreens)
        adv.appendChild(numRow('Gap between the displays', FLAT_GAP_MAX, () => S.onsite.flatGap, v => { S.onsite.flatGap = clampFlatGap(v); }));

      /* The result, drawn to scale: the frame and where the flat file lands in
         it. Cheaper to read than the two numbers. */
      const prev = el('div', 'expadv__prev');
      adv.appendChild(prev);
      const advNote = el('p', 'note');
      adv.appendChild(advNote);
      const paintAdv = () => {
        prev.textContent = '';
        const fp = flatPreview(S.onsite.flatMargin, twoScreens ? S.onsite.flatGap : 0);
        if(fp){
          const NS = 'http://www.w3.org/2000/svg';
          const svg = document.createElementNS(NS, 'svg');
          svg.setAttribute('viewBox', `0 0 ${fp.w} ${fp.h}`);
          svg.setAttribute('role', 'img');
          svg.setAttribute('aria-label', 'Where the flat file sits in the frame');
          const rect = (x, y, w2, h2, cls) => {
            const r = document.createElementNS(NS, 'rect');
            r.setAttribute('x', x); r.setAttribute('y', y); r.setAttribute('width', w2); r.setAttribute('height', h2);
            r.setAttribute('class', cls); svg.appendChild(r);
          };
          rect(0, 0, fp.w, fp.h, 'expadv__frame');
          for(const c of fp.cells) rect(c[0][0], c[0][1], c[1][0] - c[0][0], c[3][1] - c[0][1], 'expadv__cell');
          prev.appendChild(svg);
        }
        const m = +S.onsite.flatMargin.toFixed(1), g = +S.onsite.flatGap.toFixed(1);
        advNote.textContent = (m > 0
          ? `The flat file stops ${m}% of the frame's shorter side in from every edge, on black.`
          : 'The flat file fills the frame, as far as its shape allows.')
          + (twoScreens ? (g > 0 ? ` The two displays are ${g}% apart.` : ' The two displays touch.') : '');
      };
      paintAdv();

      box.onchange = () => { S.onsite.resolveToFlat = box.checked; paintNote(); };
      paintNote();
      resolveWrap.appendChild(rNote);
      resolveWrap.appendChild(adv);
      adv.classList.toggle('hidden', !S.onsite.resolveToFlat);
      box.addEventListener('change', () => adv.classList.toggle('hidden', !S.onsite.resolveToFlat));
      gSite.appendChild(resolveWrap);
    }
    /* Held back until after the blur row below, which belongs to the delivery
       video above it — putting the mockup group between a card and its own
       setting would orphan the setting. */
    card._onsiteGroup = gSite;
  }

  /* Only when video is actually on the table. A blur control above a Video
     card that says "nothing is animated yet" is a setting for a thing that
     cannot happen. */
  if (ready) gDeliver.appendChild(blurRow(vid, vidSub));
  /* THE COLOUR, SAID. Somebody opening the file in Premiere or on a signage
     player has to match its settings to the file, and the file is the only
     place that answer lives — so it is stated where the file is chosen. Video is
     tagged Rec.709 limited range (see export/video-source.js); stills are plain
     sRGB with no profile embedded. */
  gDeliver.appendChild(el('p', 'note exp__colour',
    'Colour: video is H.264, Rec.709, limited range (16–235) — the standard for Premiere, DaVinci and signage players. Stills are sRGB.'));
  if (card._onsiteGroup) card.appendChild(card._onsiteGroup);

  const foot = el('div', 'authrow authrow--split');
  foot.appendChild(el('span'));
  const cancel = el('button', 'btn btn--gh', 'Cancel');
  cancel.onclick = hide;
  foot.appendChild(cancel);
  card.appendChild(foot);

  w.appendChild(card);
  show();
}

/* ── motion blur ───────────────────────────────────────────────────────────
   Video only, and stated in units of what it costs rather than units of what
   it is: "×4" is a number nobody can price, "about 6 minutes" is the actual
   decision being made. The estimate updates as the count changes, so the
   trade is visible in the moment of making it rather than discovered while
   watching a progress bar. */

const fmtEstimate = secs => secs < 60
  ? `about ${Math.max(5, Math.round(secs / 5) * 5)} seconds`
  : `about ${Math.ceil(secs / 60)} minute${Math.ceil(secs / 60) === 1 ? '' : 's'}`;

function blurRow(vid, vidSub) {
  const wrap = el('div', 'expblur');

  const lab = el('label', 'expblur__row');
  lab.appendChild(el('span', null, 'Motion blur'));
  const sel = el('select', 'inp');
  for (const n of MOTION_BLUR_STEPS) {
    const opt = el('option', null,
      n === 1 ? 'Off' : `×${n}${n === 4 ? ' — recommended' : ''}`);
    opt.value = String(n);
    sel.appendChild(opt);
  }
  sel.value = String(motionBlurSamples());
  lab.appendChild(sel);
  wrap.appendChild(lab);

  const note = el('p', 'note');
  const paint = () => {
    const n = motionBlurSamples();
    note.textContent = n > 1
      ? `Each frame is rendered ${n} times and averaged, so anything moving smears the way a real camera's would. ` +
        `It also makes this export take ${n} times as long — ${fmtEstimate(estimateSeconds())}, against ` +
        `${fmtEstimate(estimateSeconds() / n)} without it.`
      : `Off. Fast movement will strobe slightly, the way frame-by-frame renders do. ` +
        `Turning it on multiplies the render time by the number of samples — this export is ` +
        `${fmtEstimate(estimateSeconds())} as it stands.`;
    /* The card above carries the same number, so it cannot say "MP4 · 150
       frames" while this says ×8. */
    const span = vid.querySelector('span');
    if (span) span.textContent = vidSub();
  };
  sel.onchange = () => { S.motionBlur = +sel.value; paint(); };
  paint();
  wrap.appendChild(note);
  return wrap;
}

/* exportPNG's render + downscale is synchronous, main-thread work with no
   yield point of its own — on a large resolution/supersample combination
   that can freeze the tab for a real stretch with zero visual feedback
   otherwise. Flipping the button to a disabled "Rendering…" state buys
   that feedback, but only if the browser actually gets to PAINT it before
   the freeze starts — a single requestAnimationFrame can still land in the
   same frame as this click and never draw. Two in a row (one to queue past
   the current frame, one to run after that next frame has painted) is the
   standard reliable way to guarantee a paint happened first. */
async function runPNG(btn) {
  const label = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = IMG_ICON + '<b>Rendering…</b>';
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    /* A corner screen or an LCD video wall is ONE export producing several files
       — see export/multipanel.js, which spends one credit for the whole set and
       packages them together with a README saying which goes where. */
    if (S.screenArray && arrayOutput === 'wall') {
      /* The whole wall goes through the ORDINARY single-screen path, because
         S.screen IS the wall — exportPNG already frames exactly this, and only
         the size has to be said. It is a still, so it is free; there is no gate
         to ask. */
      await exportPNG({ size: wholeWallSize() });
    } else if (S.cornerPanel || S.screenArray) {
      const r = await exportPNGPanels();
      if (!r.ok && r.reason) toast(r.reason, true);
    } else {
      await exportPNG();
    }
  } catch (err) {
    toast(err.message || 'Export failed.', true);
  } finally {
    btn.disabled = false;
    btn.innerHTML = label;
    hide();
  }
}

/* The same shape runPNG uses — the button says it is working, and says it
   again if it fails, because a still that silently does nothing is the worst
   of the possible outcomes. */
/* The on-site render's choices live in S.onsite, which travels with the project (core/doc.js), so the
   dialog being rebuilt, or the project being reopened, finds them as they were left. Only whether the
   Advanced settings accordion is open stays here: that is about the dialog, not the work. */
/* Whether the Advanced settings accordion is open — kept here so rebuilding the
   dialog (which any option change does) does not snap it shut under the cursor. */
let advOpen = false;
const pickOnsite = ({ resolveToFlat, resolveAt, stopsAt, flatMargin, flatGap }) => ({ resolveToFlat, resolveAt, stopsAt, flatMargin, flatGap });

async function runOnSitePNG(btn, filmed) {
  const was = btn.innerHTML;
  btn.innerHTML = IMG_ICON + '<b>On-site image</b><span>Rendering…</span>';
  try { await exportOnSitePNG({ filmed }); }
  catch (e) { toast(e?.message || 'That export failed.', true); }
  finally { btn.innerHTML = was; }
}
async function runOnSiteMP4(filmed) {
  /* A closure rather than the bare function: exportMP4WithUI calls whatever it
     is handed with its own progress and cancel, which is exactly the seam its
     own comment describes for a caller with a variant of its own. */
  exportMP4WithUI(opts => exportOnSiteMP4({ ...opts, ...pickOnsite(S.onsite), filmed: !!filmed && siteClipReady() }));
}

async function runMP4() {
  /* Asked here rather than discovered three seconds into a render. */
  /* Asked about the size THIS export will actually encode. A wall's S.res is
     the whole grid at tile pitch — 5823 px across for a 3 x 2 of 1080p — and
     refusing on that would have blocked an export of six 1920 files that every
     machine can manage. */
  /* peakScreenRes, not S.screenArray.res — a free layout has no one field to
     read there (it would be undefined, and this check would silently pass
     or fail on NaN × NaN). The LARGEST screen is the honest stand-in for
     "can this machine encode what this export is about to ask of it": if
     that one succeeds, every smaller screen on the same wall will too. */
  const pre = S.screenArray
    ? (arrayOutput === 'wall' ? wholeWallSize() : peakScreenRes(S.screenArray))
    : { w: S.res.w, h: S.res.h };
  if (!(await canEncodeAt(pre.w, pre.h))) {
    const { w, h } = S.screenArray ? pre : encodeSize();
    toast(`This machine will not encode video at ${w} × ${h}. Lower the output resolution — 4K is usually the ceiling.`, true);
    return;
  }
  /* A render this long is worth a word first — it is minutes of a locked-up
     tab at a high resolution, and the two things that drive it (output size
     and supersampling) are both somewhere else in the app, so someone
     hitting this has no reason to connect them to the wait. Asked BEFORE
     this screen closes: declining leaves the picker open, not a bare
     viewport with no way back to try Image instead. */
  const secs = estimateSeconds();
  if (secs > 60 && !confirm(
    `This will render ${frameCount()} frames at ${S.res.w} × ${S.res.h}, ` +
    `which should take around ${Math.ceil(secs / 60)} minutes.\n\n` +
    'Lowering Supersample in Settings, or the output resolution, makes it faster. Continue?')) return;
  hide();
  pause();
  /* A corner or an array encodes every panel in lockstep and zips them — see
     export/multipanel.js. Same dialog and cancel path; several files instead
     of one. */
  if (S.screenArray && arrayOutput === 'wall')
    exportMP4WithUI(o => exportMP4({ ...o, size: wholeWallSize() }));
  else
    exportMP4WithUI((S.cornerPanel || S.screenArray) ? exportMP4Panels : undefined);
}

export function initExport() {
  const btn = $('#btnExport');
  if (btn) btn.onclick = showExport;
}
